// buildFlightInfo — pure assembly of the displayed flightInfo object.
//
// Extracted verbatim from `FlightLookup.js` (module-level `buildFlightInfo`) as
// part of the behavior-preserving refactor (issue #240). Maps a matched
// FlightAware flight plus the user's airline/flightNumber inputs into the
// normalized `flightInfo` shape rendered by the result panel.
//
// Field mapping and fallbacks are reproduced exactly (gate/terminal via
// `resolveGateInfo`, airports via `extractAirportCode`, times via
// `resolveFlightTime`, `flightIdent` = `flight.flightNumber || flight.ident`,
// `aircraftType` = `flight.aircraft?.type || flight.aircraft_type`, and the raw
// flight retained under `rawData`). No guards beyond the source's.
//
// React-free and side-effect-free.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

import resolveGateInfo from './resolveGateInfo.js';
import extractAirportCode from './extractAirportCode.js';
import resolveFlightTime from './resolveFlightTime.js';

/**
 * Builds the flightInfo object from a matched flight and user inputs.
 *
 * @param {Object} flight - The matched FlightAware flight.
 * @param {string} airline - The user-entered airline code.
 * @param {string} flightNumber - The user-entered flight number.
 * @returns {Object} The normalized flightInfo object.
 */
export function buildFlightInfo(flight, airline, flightNumber) {
  const depGate = resolveGateInfo(flight, 'departure');
  const arrGate = resolveGateInfo(flight, 'arrival');

  return {
    airline: airline,
    flightNumber: flightNumber,
    flightIdent: flight.flightNumber || flight.ident,
    departureAirport: extractAirportCode(flight, 'departure'),
    arrivalAirport: extractAirportCode(flight, 'arrival'),
    departureTime: resolveFlightTime(flight, 'departure'),
    arrivalTime: resolveFlightTime(flight, 'arrival'),
    departureGate: depGate.gate,
    arrivalGate: arrGate.gate,
    departureTerminal: depGate.terminal,
    arrivalTerminal: arrGate.terminal,
    status: flight.status,
    aircraftType: flight.aircraft?.type || flight.aircraft_type,
    rawData: flight
  };
}

export default buildFlightInfo;
